import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { hasRole, useAuth } from "../lib/auth.js";
import {
  awaitsConfirmationFrom,
  confirmMatch,
  disputeMatch,
  voidMatch,
  type ConfirmResult,
} from "../lib/matches.js";
import type { Member, MatchSummary } from "../lib/types.js";
import { FixScore } from "./FixScore.js";
import { Button, inputStyle, Notice } from "./ui.js";

/**
 * Everything you can do to one match, wherever the match is shown.
 *
 * This used to live inside the match log, which meant the event's own results screen — the
 * one an organiser actually has open on the night — listed matches saying "awaiting
 * confirmation" with no way to confirm them, and no way to correct a typo either. The rules
 * are not the screen's to decide, so rather than a second copy of them, the controls moved
 * here and both screens render the same ones.
 *
 * Who sees what is the database's answer restated:
 *
 *   * **Confirm** — an admin may confirm anything (`confirm-match` treats them as
 *     authoritative and audits it, because they enter scores for players who have gone home
 *     and a match nobody can confirm never reaches the rating). Everybody else has to be a
 *     participant on the side that did not enter it.
 *   * **Something's wrong** — anybody who could confirm can dispute instead.
 *   * **Cancel match** — organisers, matching `void_match`.
 *   * **Fix the score** — organisers, matching `correct_match`. Not offered on a cancelled
 *     match, which already says it never happened.
 */
/**
 * Whether this person could confirm this match if they tapped it.
 *
 * `confirm-match` decides for real; this is the same rule, so the button is never one that
 * comes back refused. An **admin may confirm anything** — they enter scores for players who
 * have gone home, and a match nobody confirms never reaches the rating engine at all, which
 * is how a tournament's results end up sitting there unrated for a week. Everybody else has
 * to be a participant on the side that did not enter it.
 */
export const confirmableBy = (match: MatchSummary, member: Member | null) =>
  match.status === "pending" &&
  member !== null &&
  (hasRole(member, "admin") || awaitsConfirmationFrom(match, member.id));

export function MatchTools({
  match,
  onDone,
}: {
  match: MatchSummary;
  /** Called after anything lands, so the screen can re-read whatever it shows. */
  onDone: () => void;
}) {
  const { member } = useAuth();
  const canOrganise = hasRole(member, "organiser");
  const isAdmin = hasRole(member, "admin");

  // `delta: null` is a confirmation that moved nothing on purpose — a mixed match. Not the
  // same as no result at all, which is why it is a null rather than an absence.
  const [result, setResult] = useState<{ delta: number | null } | null>(null);
  const [fixing, setFixing] = useState(false);

  const confirm = useMutation({
    mutationFn: () => confirmMatch(match.id),
    onSuccess: (confirmed: ConfirmResult) => {
      const change = confirmed.changes.find((c) => c.member_id === member?.id);
      setResult({ delta: confirmed.rated && change ? change.delta : null });
      onDone();
    },
  });

  const dispute = useMutation({
    mutationFn: (reason: string) => disputeMatch(match.id, reason),
    onSuccess: onDone,
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => voidMatch(match.id, reason),
    onSuccess: onDone,
  });

  const busy = confirm.isPending || dispute.isPending || cancel.isPending;
  const failure = confirm.error ?? dispute.error ?? cancel.error;

  return (
    <>
      {confirmableBy(match, member) ? (
        <Confirmation
          busy={busy}
          canCancel={canOrganise}
          // Confirmed is not the same as rated: a mixed match, or one from an event that does
          // not count, reaches 'confirmed' having moved nobody, so cancelling it needs no
          // recompute.
          rated={match.status === "confirmed" && match.movesRatings}
          onConfirm={() => confirm.mutate()}
          onDispute={(reason) => dispute.mutate(reason)}
          onCancel={(reason) => cancel.mutate(reason)}
        />
      ) : null}

      {/* Gains glow, losses are stated plainly — the same rule the rating lockup follows. */}
      {result ? (
        result.delta === null ? (
          <p className="mt-2 text-sm text-ink-muted">Confirmed.</p>
        ) : (
          <p
            className="font-mono mt-2 text-sm"
            style={{ color: result.delta >= 0 ? "var(--color-up)" : "var(--color-down)" }}
          >
            Confirmed. Your rating moved{" "}
            <span className={result.delta >= 0 ? "highlight px-1" : ""}>
              {result.delta >= 0 ? "▲" : "▼"}{" "}
              {(Math.abs(result.delta) / 200).toFixed(3).replace(/^0/, "")}
            </span>
          </p>
        )
      ) : null}

      {canOrganise && match.status !== "void" ? (
        fixing ? (
          <FixScore
            match={match}
            onCancel={() => setFixing(false)}
            onDone={() => {
              setFixing(false);
              onDone();
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setFixing(true)}
            className="micro mt-2 underline"
            style={{ minHeight: 44 }}
          >
            Fix the score
          </button>
        )
      ) : null}

      {failure ? <Notice tone="attention">{failure.message}</Notice> : null}
    </>
  );
}

/**
 * Confirm is the whole point; dispute is the escape hatch and is styled like one. Cancel is
 * a third thing and deliberately not a louder dispute: a dispute says the score is wrong and
 * asks someone to sort it out, cancelling says there is nothing to sort out. Only an
 * organiser sees it, because only an organiser can do it.
 */
export function Confirmation({
  busy,
  canCancel,
  rated,
  onConfirm,
  onDispute,
  onCancel,
}: {
  busy: boolean;
  canCancel: boolean;
  rated: boolean;
  onConfirm: () => void;
  onDispute: (reason: string) => void;
  onCancel: (reason: string) => void;
}) {
  const [reason, setReason] = useState<string | null>(null);
  const [mode, setMode] = useState<"dispute" | "cancel">("dispute");

  const open = (next: "dispute" | "cancel") => {
    setMode(next);
    setReason("");
  };

  return (
    <div className="mt-3">
      {reason === null ? (
        // Confirm and dispute side by side: two answers to one question. Confirm takes the
        // room; dispute keeps its full sentence at its own width.
        // Cancel is a different question, so it gets its own line.
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
          <Button tone="primary" onClick={onConfirm} disabled={busy}>
            {busy ? "Confirming…" : "Confirm"}
          </Button>
          <Button
            tone="attention"
            onClick={() => open("dispute")}
            disabled={busy}
            style={{ padding: "0 14px", whiteSpace: "nowrap" }}
          >
            Something&rsquo;s wrong
          </Button>
          {canCancel ? (
            <Button
              className="col-span-2"
              tone="quiet"
              onClick={() => open("cancel")}
              disabled={busy}
            >
              Cancel match
            </Button>
          ) : null}
        </div>
      ) : (
        <div>
          <input
            autoFocus
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={mode === "cancel" ? "Why is it being cancelled?" : "What's wrong with it?"}
            style={inputStyle}
          />
          <div className="mt-3 flex gap-3">
            <Button
              tone="attention"
              onClick={() =>
                mode === "cancel"
                  ? onCancel(reason.trim() || "No reason given")
                  : onDispute(reason.trim() || "No reason given")
              }
              disabled={busy}
            >
              {mode === "cancel" ? "Cancel it" : "Raise it"}
            </Button>
            <Button onClick={() => setReason(null)}>Back</Button>
          </div>
          <p className="mt-2 text-sm text-ink-muted">
            {mode === "cancel"
              ? rated
                ? "The match stops counting for everyone. It had already been rated, so run a recompute afterwards to put the numbers right."
                : "The match stops counting for everyone. Nothing has been rated yet, so no rating changes."
              : "An organiser sorts it out. Nothing changes anyone’s rating in the meantime."}
          </p>
        </div>
      )}
    </div>
  );
}
